import * as R from 'ramda'
import { distinctUntilChanged, ignoreElements, filter, pairwise, tap } from 'rxjs/operators'
import { combineEpics, ofType } from 'redux-observable'
import { LOCATION_CHANGE } from 'connected-react-router'
import * as Actions from 'store/actions/index.ts'
import {
  getBeginDate,
  getCities,
  getDepartureDatesMPRanges,
  getHasDepartureDateFlexibilityBeenUsed,
  getSelectedDepartureDateOffsetFromQuery,
  getSelectedCalendarDepartureDatesFlexibility
} from 'store/selectors'
import { track } from 'tracking/mixPanel'
import { ALL_FILTERS_KEYS } from 'constants/filters'
import { getDepartureCitiesFromCode } from 'domain/pricerDepartureCity'
import {
  areValuesDifferent,
  getDepartureDatesFromOffset,
  getFiltersFromQuery,
  pathnameHasNotChanged
} from 'domain/filters'
import { mapStateToMPEvent as getViewCatalogPageData } from './viewCatalogPage'

const EVENT_FILTER = 1
const EVENT_RESET = 2

const getMPSettingsFromFiltersDiff = R.pipe(
  R.map(getFiltersFromQuery),
  R.curry(filters => (
    R.reduce((acc, key) => {
      if (filters[0][key] !== filters[1][key]) {
        if (filters[1][key]) {
          acc = { filters: filters[1][key], key, type: EVENT_FILTER }
        } else {
          acc = { filters: filters[0][key], key, type: EVENT_RESET }
        }
      }
      return acc
    }, { key: '', type: 0 }, ALL_FILTERS_KEYS)
  ))
)

const hasFiltersChanged = R.pipe(
  R.map(getFiltersFromQuery),
  areValuesDifferent
)

const shouldTrackEvent = pair => R.both(
  pathnameHasNotChanged,
  hasFiltersChanged
)(pair)

const convertFiltersDepartureCities = (state) => R.pipe(
  R.ifElse(
    R.pathEq(['key'], 'departureCities'),
    R.pipe(
      ({ values = [] }) => getDepartureCitiesFromCode(getCities(state))(values),
      R.defaultTo([])
    ),
    R.always(undefined)
  )
)

const convertFiltersDepartureDates = (state) => R.pipe(
  R.ifElse(
    R.pathEq(['key'], 'departureDates'),
    R.pipe(
      () => getDepartureDatesFromOffset(getBeginDate(state), 'YYYY-MM-DDTHH:mm:ss')([getSelectedDepartureDateOffsetFromQuery(state)]),
      R.defaultTo([])
    ),
    R.always(undefined)
  )
)

const convertFiltersDurations = (state) => R.pipe(
  R.ifElse(
    R.pathEq(['key'], 'durations'),
    R.pipe(
      ({ values = [] }) => values.map(durations => `${durations} nights`),
      R.defaultTo([])
    ),
    R.always(undefined)
  )
)

const convertFilterKeyToMPData = R.cond([
  [R.equals('departureCities'), R.always('Departure City')],
  [R.equals('departureDates'), R.always('Departure Date')],
  [R.equals('durations'), R.always('Duration')]
])

const mapStateToMPEvent = (
  eventName,
  state,
  { payload: { data, eventType, isQuickFilter } } // { payload: { data, eventType, isQuickFilter } }: FilterCatalogAction
) => {
  const selectedDepartureDateSettings = (eventType === 'Filter' && data.key === 'departureDates' && data.values) || []
  const selectedDepartureDateOffset = ((selectedDepartureDateSettings.length > 0) && Number(selectedDepartureDateSettings[0])) || 0
  const departureDatesRanges = (selectedDepartureDateOffset > 0) ? getDepartureDatesMPRanges(state) : []
  const departureSelectedOption = departureDatesRanges.filter(option => option.value[0] === Number(selectedDepartureDateOffset))
  const departureSelectedLabel = (departureSelectedOption.length > 0) ? departureSelectedOption[0].label : undefined

  const dateFlexibility = getHasDepartureDateFlexibilityBeenUsed(state) ? getSelectedCalendarDepartureDatesFlexibility(state) : undefined

  const mpData = {
    ...getViewCatalogPageData(eventName, state),
    'Date Flexibility': dateFlexibility,
    'Filters Category': convertFilterKeyToMPData(data.key),
    'Interaction Type': 'Click',
    'Quick Filter On': isQuickFilter,
    'Stage': eventType
  }

  const departureCities = convertFiltersDepartureCities(state)({ ...data, eventType })
  const departureDates = convertFiltersDepartureDates(state)({ ...data, eventType })
  const durations = convertFiltersDurations(state)({ ...data, eventType })

  return (
    eventType !== 'Access'
      ? Object.assign({}, mpData, {
        'Filter City': departureCities,
        'Filter Date': departureSelectedLabel || departureDates,
        'Filter Duration': durations
      })
      : mpData
  )
}

const filtersSelectionEpic = (
  actions$,
  state$
) => actions$.pipe(
  ofType(LOCATION_CHANGE),
  pairwise(),
  filter(shouldTrackEvent),
  tap(action => {
    const { filters, key, type } = getMPSettingsFromFiltersDiff(action)
    const values = filters ? filters.split(',') : undefined
    const eventType = (type === EVENT_FILTER) ? 'Filter' : 'Reinitialize'
    const eventName = 'Use Catalogue Filters'

    track(
      eventName,
      mapStateToMPEvent(
        eventName,
        state$.value,
        R.assocPath(['payload'], { data: { key, values }, eventType, isQuickFilter: false }, action)
      ),
      { debug: !__PRODUCTION__ }
    )
  }),
  ignoreElements()
)

const filtersAccessAndResetEpic = (
  actions$,
  state$
) => actions$.pipe(
  ofType(Actions.catalog.FILTER_CATALOG),
  distinctUntilChanged(),
  tap(action => {
    const eventName = 'Use Catalogue Filters'

    track(
      eventName,
      mapStateToMPEvent(eventName, state$.value, action),
      { debug: !__PRODUCTION__ }
    )
  }),
  ignoreElements()
)

export default combineEpics(filtersAccessAndResetEpic, filtersSelectionEpic)
